/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import {ProductInfoBase} from "abweb/dto/generated/product/ProductInfoBase";
import {FetchUtils} from "abweb/service/utils/rpc/FetchUtils";

interface ProductInfo
{
  name: string,
  version: string,
}

interface PlatformInfo
{
  name: string,
  officialName?: string,
  description: string,
  version: string,
}

interface ContentInfo
{
  tutorials: boolean
}

interface PlatformPortalInfoData
{
  products: ProductInfo[],
  platform: PlatformInfo,
  content: ContentInfo
}

export class PlatformPortalInfo
{
  private static _data: PlatformPortalInfoData;
  private static _portalProductInfo: ProductInfoBase;

  private constructor()
  {
  }

  static async initialize(): Promise<void>
  {
    if (PlatformPortalInfo._data === undefined)
    {
      // Fetch the Portal's own product info
      const portalProductInfoJson: ProductInfoBase = await FetchUtils.fetchAndParseJsonFile("webapp/app/assets/productInfo.json");
      PlatformPortalInfo._portalProductInfo = portalProductInfoJson;

      // Now fetch the Data Platform's product info
      const infoData: PlatformPortalInfoData = await FetchUtils.fetchAndParseJsonFile(
        "webapp/app/assets/platform-portal/abinitio-platform.json",
        {cache: "no-cache"});
      const updatedInfoData = PlatformPortalInfo.updatePlatformPortalInfoData(infoData);
      PlatformPortalInfo._data = updatedInfoData;
    }
  }

  private static updatePlatformPortalInfoData(data: PlatformPortalInfoData): PlatformPortalInfoData
  {
    const isDevelopmentPortal = PlatformPortalInfo.portal.version.indexOf("SNAPSHOT") > -1;
    // If a short name isn't provided, remove "Ab Initio" from the name, e.g. "Ab Initio Data Platform"
    const platformName = data.platform.name; // Something like "Ab Initio Agentic Data Service"
    const name = platformName.startsWith("Ab Initio")
      ? platformName.replace("Ab Initio ", "")
      : platformName;
    const officialName = data.platform.officialName ?? platformName;
    // If this is a snapshot portal, then ensure that the platform portal is also declared as a snapshot.
    // Note: this is a workaround to CPT-1635, but it should be safe to keep this logic
    const version = isDevelopmentPortal && data.platform.version.indexOf("SNAPSHOT") === -1
      ? `${data.platform.version}-SNAPSHOT`
      : data.platform.version;
    return {
      ...data,
      platform: {
        ...data.platform,
        name: name,
        officialName: officialName,
        version: version
      }
    }
  }

  private static getData(): PlatformPortalInfoData
  {
    if (PlatformPortalInfo._data === undefined)
    {
      throw new Error("Platform Portal Info not initialized");
    }
    return PlatformPortalInfo._data
  }

  static get content(): ContentInfo
  {
    return PlatformPortalInfo.getData().content
  }

  static get products(): ProductInfo[]
  {
    return PlatformPortalInfo.getData().products
  }

  static get platform(): PlatformInfo
  {
    return PlatformPortalInfo.getData().platform
  }

  static get portal(): ProductInfoBase
  {
    return PlatformPortalInfo._portalProductInfo;
  }
}
